import { observer } from 'mobx-react-lite';
import { FC, ReactElement, useCallback, useMemo } from 'react';

import {
  AvatarPlaceholderType,
  GROUPS_PLACEHOLDER_ITEM_SIZE,
  GroupsPlaceholder,
} from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';

import { VirtualizedList } from 'APP/components/VirtualizedList/VirtualizedList';

import { ChannelItem } from './ChannelItem/ChannelItem';
import { GroupItem } from './GroupItem/GroupItem';
import { useGroupListPresenter, IGroupListPresenterParams } from './GroupList.presenter';
import styles from './GroupList.styles.m.css';

const GROUP_ITEM_HEIGHT = 64;
const PLACEHOLDER_ITEMS_COUNT = 14;

type IGroupItemProps = IGroupListPresenterParams;

export const GroupList: FC<IGroupItemProps> = observer((props) => {
  const { t } = useTranslation();
  const presenter = useGroupListPresenter(props);

  const getListItem = useCallback(
    (index: number): ReactElement => {
      const group = presenter.visibleGroups[index];

      return group.isChannel ? (
        <ChannelItem
          isShowSpaceIcon
          group={group}
        />
      ) : (
        <GroupItem
          isShowSpaceIcon
          group={group}
        />
      );
    },
    [presenter.groupHash]
  );

  const Placeholder = useMemo(() => {
    return (
      <GroupsPlaceholder
        count={PLACEHOLDER_ITEMS_COUNT}
        avatarType={AvatarPlaceholderType.Round_10}
      />
    );
  }, []);

  return (
    <div className={styles.container}>
      {presenter.isShowGroupsList ? (
        <div className={styles.list}>
          <VirtualizedList
            itemCount={presenter.visibleGroups.length}
            getListItem={getListItem}
            itemSize={GROUP_ITEM_HEIGHT}
            isShowLoader={presenter.isShowPlaceholder}
            Loader={Placeholder}
            loaderSize={PLACEHOLDER_ITEMS_COUNT * GROUPS_PLACEHOLDER_ITEM_SIZE}
          />
        </div>
      ) : (
        <div className={styles.emptyListStub}>{t('talks_empty')}</div>
      )}
    </div>
  );
});
